import React from "react";
import { Plus, Loader2 } from "lucide-react";

/**
 * QuickAddPnr — compact strip above the trend chart for adding a PNR.
 *
 * Controlled component: receives its state from Dashboard.jsx so the
 * existing handleAddPnr / bulk logic keeps working unchanged.
 *
 * Testid preservation: pnr-input, add-pnr-btn, bulk-pnr-input,
 * toggle-bulk-mode — all kept verbatim.
 *
 * Props:
 *   newPnr, setNewPnr     — single-mode input state
 *   bulkMode, setBulkMode — toggle between single and bulk
 *   bulkPnrs, setBulkPnrs — bulk-mode textarea state
 *   onSubmit              — form submit handler (also called by the
 *                           Track button onClick for Enter-key support)
 *   isLoading             — disables inputs + shows spinner on the CTA
 */

const QuickAddPnr = ({
  newPnr,
  setNewPnr,
  bulkMode = false,
  setBulkMode,
  bulkPnrs,
  setBulkPnrs,
  onSubmit,
  isLoading = false,
}) => {
  return (
    <form
      onSubmit={onSubmit}
      className="relative rounded-xl border border-zinc-800/70 bg-gradient-to-br from-indigo-500/[0.05] via-zinc-900/40 to-zinc-900/40 p-4 overflow-hidden"
      data-testid="quick-add-pnr"
    >
      <div className="absolute -top-20 -right-10 w-60 h-60 bg-indigo-500/10 blur-3xl rounded-full pointer-events-none" />

      <div className="relative flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-center gap-3 min-w-0 lg:flex-1">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center shrink-0">
            <Plus size={18} className="text-indigo-300" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[12.5px] font-semibold text-zinc-100 whitespace-nowrap">
                Track a new PNR
              </span>
              <button
                type="button"
                onClick={() => setBulkMode?.(!bulkMode)}
                data-testid="toggle-bulk-mode"
                className="text-[10.5px] text-zinc-500 hover:text-indigo-300 underline-offset-2 hover:underline transition-colors"
              >
                {bulkMode ? "Single PNR" : "Add multiple"}
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
              {bulkMode
                ? "Paste up to 20 PNRs — comma- or newline-separated"
                : "Paste a 10-digit PNR — we’ll fetch live status, sync calendar, generate wallet passes"}
            </div>
          </div>
        </div>

        <div className="flex items-stretch gap-2 lg:shrink-0">
          {bulkMode ? (
            <textarea
              placeholder="1234567890&#10;0987654321&#10;…"
              value={bulkPnrs || ""}
              onChange={(e) => setBulkPnrs?.(e.target.value)}
              disabled={isLoading}
              data-testid="bulk-pnr-input"
              className="bg-zinc-950 border border-zinc-800 rounded-lg text-zinc-50 text-[12px] px-3 py-2 w-full lg:w-[280px] h-[68px] resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500/50 placeholder:text-zinc-600 font-mono tracking-[0.06em]"
            />
          ) : (
            <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden focus-within:ring-1 focus-within:ring-indigo-500/50 focus-within:border-indigo-500/50">
              <input
                type="text"
                inputMode="numeric"
                placeholder="0000000000"
                value={newPnr || ""}
                onChange={(e) =>
                  setNewPnr?.(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                disabled={isLoading}
                data-testid="pnr-input"
                className="bg-transparent text-zinc-50 text-[13px] px-3 py-2 w-[180px] focus:outline-none placeholder:text-zinc-600 font-mono tracking-[0.1em]"
              />
              <span className="text-[10px] text-zinc-600 pr-2.5 font-mono shrink-0">
                {(newPnr || "").length}/10
              </span>
            </div>
          )}
          <button
            type="submit"
            disabled={isLoading}
            data-testid="add-pnr-btn"
            className="h-9 px-4 self-end rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white text-[12.5px] font-medium shadow-[0_0_22px_rgba(99,102,241,.35)] flex items-center gap-1.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} strokeWidth={2.4} />
            )}
            Track
          </button>
        </div>
      </div>
    </form>
  );
};

export default QuickAddPnr;
